import React from "react";
import { Box, Button, CircularProgress, TextField, Typography } from "@mui/material";
import IconCircle from "../../shared/IconCircle";
import { cardDescSx, cardTitleSx, COLORS, CONTENT_MAX_WIDTH, FONT } from "../../shared/styles";
import { EmailIcon } from "../../LinkEmail/icons";
import { EXAMPLE_BUSINESS_EMAIL, FOOTER_NOTE } from "./constants";

const FIELD_SX = {
  width: "100%",
  height: "40px",
  borderRadius: "8px",
  fontFamily: FONT.family,
  fontSize: "14px",
  fontWeight: 400,
  boxSizing: "border-box",
};

function BusinessEmailInfoCard({
  emailInput,
  onEmailChange,
  onLink,
  linkDisabled,
  saveLoading,
  isLinked,
  linkedEmail,
}) {
  const displayEmail = isLinked ? linkedEmail : EXAMPLE_BUSINESS_EMAIL;

  return (
    <Box
      sx={{
        mt: "32px",
        width: "100%",
        maxWidth: CONTENT_MAX_WIDTH,
        minHeight: "160px",
        p: "16px",
        borderRadius: "8px",
        border: `1px solid ${COLORS.primary}`,
        bgcolor: "#F3FBFF",
        boxShadow: "0 1px 2px 0 rgba(20, 141, 253, 0.10)",
        boxSizing: "border-box",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: "16px" }}>
        <IconCircle bgcolor="#D6EDFF">
          <EmailIcon />
        </IconCircle>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography component="p" sx={cardTitleSx}>
            How Custom Domains Work
          </Typography>
          <Typography component="p" sx={{ ...cardDescSx, mt: "4px", lineHeight: "24px" }}>
            Link a group-specific business email so outbound replies use:
          </Typography>

          <Box sx={{ mt: "16px" }}>
            {isLinked ? (
              <Box
                sx={{
                  ...FIELD_SX,
                  px: "12px",
                  display: "flex",
                  alignItems: "center",
                  border: "1px solid #D6EDFF",
                  bgcolor: "#FAFDFF",
                  color: COLORS.textSecondary,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {displayEmail}
              </Box>
            ) : (
              <TextField
                type="email"
                placeholder="e.g. support@company.com"
                value={emailInput}
                onChange={(e) => onEmailChange(e.target.value)}
                disabled={linkDisabled}
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    ...FIELD_SX,
                    color: COLORS.textPrimary,
                    boxShadow: COLORS.shadow,
                    background: COLORS.white,
                    "& fieldset": {
                      borderColor: COLORS.border,
                    },
                    "&:hover fieldset": {
                      borderColor: COLORS.border,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: COLORS.primary,
                    },
                    "&.Mui-disabled": {
                      bgcolor: COLORS.backgroundLight,
                    },
                  },
                  "& .MuiOutlinedInput-input": {
                    py: 0,
                    px: "12px",
                    "&::placeholder": {
                      color: COLORS.textPlaceholder,
                      opacity: 1,
                    },
                  },
                }}
              />
            )}

            <Typography
              component="p"
              sx={{
                mt: "8px",
                mb: 0,
                color: COLORS.textSecondary,
                fontFamily: FONT.family,
                fontSize: "12px",
                fontWeight: 400,
                lineHeight: "18px",
              }}
            >
              {FOOTER_NOTE}
            </Typography>

            {!isLinked ? (
              <Button
                onClick={onLink}
                disabled={linkDisabled}
                variant="contained"
                disableElevation
                sx={{
                  mt: "12px",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: "120px",
                  height: "36px",
                  px: "16px",
                  borderRadius: "8px",
                  bgcolor: COLORS.primary,
                  backgroundColor: COLORS.primary,
                  color: COLORS.white,
                  fontFamily: FONT.family,
                  fontSize: "14px",
                  fontWeight: 500,
                  lineHeight: "22px",
                  textTransform: "none",
                  boxShadow: COLORS.shadow,
                  "&:hover": {
                    bgcolor: "#127EDE",
                    backgroundColor: "#127EDE",
                    boxShadow: COLORS.shadow,
                  },
                  "&:disabled": { opacity: 0.6 },
                }}
              >
                {saveLoading ? (
                  <CircularProgress size={18} sx={{ color: COLORS.white }} />
                ) : (
                  "Link Email"
                )}
              </Button>
            ) : null}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default BusinessEmailInfoCard;
